import { useQuery } from "@apollo/client";
import ContentCopy from "@mui/icons-material/ContentCopy";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ReadMore from "@mui/icons-material/ReadMore";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { ScoutInteraction } from "generated-graphql/graphql";
import { useScrollToDeepLinkTarget } from "hooks/useScrollToDeepLinkTarget";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";

import { AgentMessage } from "../../Customer/Scout/Chat/message/AgentMessage";
import { UserMessage } from "../../Customer/Scout/Chat/message/UserMessage";
import { DATA_CHANGE_PROPOSAL_KIND } from "../../Customer/Scout/Chat/scoutDataChangeProposal";
import { LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND } from "../../Customer/Scout/Chat/scoutLargeFileFullAnalysisProposal";
import { ScoutProposalProvider } from "../../Customer/Scout/Chat/ScoutProposalContext";
import { TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND } from "../../Customer/Scout/Chat/scoutTenantDocumentProposal";
import { useScoutConversation } from "../../Customer/Scout/hooks/useScoutConversation";
import { ScoutDeepLinkProvider } from "../../Customer/Scout/ScoutDeepLinkContext";
import { parseScoutDeepLinkHash } from "../../Customer/Scout/scoutDeepLinks";
import { ScoutConversationContextAnalysis } from "./ScoutConversationContextAnalysis";
import { GET_SCOUT_CONVERSATION_HISTORY_FOR_STAFF } from "./scoutObserverHistoryLoader";

const noopAsync = async () => {};

// Tab tokens persisted on the URL so the back/forward stack remembers which
// view the staff user was last looking at. "chat" is the default for the
// initial drill-down; "context" is the analysis tab.
type ViewerTab = "chat" | "context";

function readTabParam(value: string | null): ViewerTab {
  return value === "context" ? "context" : "chat";
}

interface Props {
  conversationId: string;
}

const STAFF_OBSERVER_TENANT_PLACEHOLDER = "staff-observer";
// Match Customer/Scout/Chat/ChatChrome's content max width so the staff
// observer's header chips line up with the messages below them.
const CONTENT_MAX_WIDTH_PX = 1000;
const CONTENT_X_PADDING = { xs: 2, sm: 3 };

function CopyChip({ label, value }: { label: string; value: string }) {
  return (
    <Tooltip title={`Copy ${label}`} placement="top">
      <Chip
        size="small"
        variant="outlined"
        icon={<ContentCopy sx={{ fontSize: 12 }} />}
        label={
          <Typography variant="caption" component="span" sx={{ fontFamily: "monospace" }}>
            {label}: {truncateMiddle(value, 28)}
          </Typography>
        }
        onClick={() => {
          if (typeof navigator !== "undefined" && navigator.clipboard) {
            void navigator.clipboard.writeText(value);
          }
        }}
        sx={{ cursor: "pointer" }}
      />
    </Tooltip>
  );
}

function truncateMiddle(value: string, max: number): string {
  if (value.length <= max) {
    return value;
  }

  const head = Math.ceil((max - 1) / 2);
  const tail = Math.floor((max - 1) / 2);
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

export function ScoutConversationViewer({ conversationId }: Props) {
  const theme = useTheme();
  const canvasColor = theme.palette.canvas;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const appBarSpacing = isMobile ? xsHeight : mdHeight;
  const scrollbarThumbColor = theme.palette.scrollbarThumb;

  const location = useLocation();
  const deepLinkTarget = useMemo(() => parseScoutDeepLinkHash(location.hash), [location.hash]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Header data only — message rendering is driven by useScoutConversation
  // below, which reads the staff controller's store (continuously updated by
  // the SSE observer). The two share the same Apollo cache and the staff
  // history loader uses the same query, so a cache-then-refetch policy lets
  // them collapse into a single network request on first paint while still
  // pulling fresh `usage` rollups every visit. cache-first was wrong here:
  // live message rendering does flow via SSE, but the conversation-level
  // usage / cost rollup only updates when the GraphQL query reissues — so
  // a follow-up turn would never refresh the header chip totals.
  const headerQuery = useQuery(GET_SCOUT_CONVERSATION_HISTORY_FOR_STAFF, {
    variables: { conversationId },
    fetchPolicy: "cache-and-network",
  });

  // The staff stream URL builder ignores tenantId, so any non-empty value is
  // fine here. Pass the real tenant id when we have it for log clarity, fall
  // back to a placeholder otherwise so the controller actually attaches.
  const conversationTenantId =
    headerQuery.data?.getScoutConversationHistoryForStaff?.tenant?.id ??
    STAFF_OBSERVER_TENANT_PLACEHOLDER;
  const { conversation, messages, loading, error, streamStatus, respondedDataChangeProposals } =
    useScoutConversation({
      tenantId: conversationTenantId,
      conversationId,
    });

  // Read-only proposal surface: supply responded state so Approved/Rejected
  // summaries render, but never expose Accept/Reject (see readOnly flag).
  const proposalContextValue = useMemo(
    () => ({
      readOnly: true as const,
      handlers: {
        [TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND]: {
          respondedProposals: {},
          isProcessing: false,
          onConfirm: noopAsync,
          onDecline: noopAsync,
        },
        [LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND]: {
          respondedProposals: {},
          isProcessing: false,
          onConfirm: noopAsync,
          onDecline: noopAsync,
        },
        [DATA_CHANGE_PROPOSAL_KIND]: {
          respondedProposals: respondedDataChangeProposals,
          isProcessing: false,
          onResolve: noopAsync,
          dataChanges: conversation?.dataChanges,
        },
      },
    }),
    [conversation?.dataChanges, respondedDataChangeProposals]
  );

  useEffect(() => {
    document.title = "Scout Observer — Encamp Staff";
    return () => {
      document.title = "Encamp";
    };
  }, []);

  const header = headerQuery.data?.getScoutConversationHistoryForStaff;
  const showAuthorEmail = header?.interaction === ScoutInteraction.FrontEmail;
  const isInitialLoading = headerQuery.loading && !header;
  const headerError = headerQuery.error;

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = readTabParam(searchParams.get("tab"));
  const handleTabChange = (_: unknown, next: ViewerTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === "chat") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", next);
    }
    setSearchParams(nextParams, { replace: true });
  };

  useScrollToDeepLinkTarget({
    rootRef: scrollContainerRef,
    targetId: deepLinkTarget?.id,
  });

  return (
    <ScoutDeepLinkProvider target={deepLinkTarget}>
      <Stack
        sx={{
          // Fill the viewport below the app bar so the canvas color extends
          // edge-to-edge — no white gap below.
          position: "relative",
          width: "100%",
          height: `calc(100vh - ${theme.spacing(appBarSpacing)})`,
          overflow: "hidden",
          backgroundColor: canvasColor,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: canvasColor,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              maxWidth: CONTENT_MAX_WIDTH_PX,
              width: "100%",
              mx: "auto",
              px: CONTENT_X_PADDING,
              py: 2,
            }}
          >
            <ConversationHeader
              loading={isInitialLoading}
              title={conversation?.title ?? header?.title}
              tenantName={header?.tenant?.name}
              tenantId={header?.tenant?.id}
              userEmail={header?.user?.email}
              userId={header?.user?.id}
              userName={
                header?.user
                  ? [header.user.firstName, header.user.lastName].filter(Boolean).join(" ") ||
                    undefined
                  : undefined
              }
              status={header?.status}
              createdAt={header?.createdAt}
              updatedAt={header?.updatedAt}
              conversationId={conversationId}
              streamStatus={streamStatus}
              usage={header?.usage}
            />
          </Box>
          <Box
            sx={{
              maxWidth: CONTENT_MAX_WIDTH_PX,
              width: "100%",
              mx: "auto",
              px: CONTENT_X_PADDING,
            }}
          >
            <Tabs
              value={tab}
              onChange={handleTabChange}
              sx={{
                minHeight: 40,
                "& .MuiTab-root": { minHeight: 40, py: 0.5 },
              }}
            >
              <Tab value="chat" label="Chat" />
              <Tab value="context" label="Context analysis" />
            </Tabs>
          </Box>
        </Box>
        {(headerError || error) && (
          <Box
            sx={{
              flexShrink: 0,
              maxWidth: CONTENT_MAX_WIDTH_PX,
              width: "100%",
              mx: "auto",
              px: CONTENT_X_PADDING,
              py: 1,
            }}
          >
            {headerError ? (
              <Alert severity="error" sx={{ mb: error ? 1 : 0 }}>
                Failed to load conversation: {headerError.message}
              </Alert>
            ) : null}
            {error ? <Alert severity="warning">Stream error: {error.message}</Alert> : null}
          </Box>
        )}
        <Box
          component="main"
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            // Chat tab is a vertical scroll container (long thread). Context
            // tab owns its own internal scrolling (DataGrid virtualization +
            // donut sidebar) and needs `overflow: hidden` here so the outer
            // box doesn't introduce a second scroll layer that breaks the
            // DataGrid's sticky header.
            overflowY: tab === "context" ? "hidden" : "auto",
            overflowX: "hidden",
            backgroundColor: canvasColor,
            scrollbarColor: `${scrollbarThumbColor} ${canvasColor}`,
            "&::-webkit-scrollbar": { backgroundColor: canvasColor },
            "&::-webkit-scrollbar-track": { backgroundColor: canvasColor },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: scrollbarThumbColor,
              border: "none",
              borderRadius: 999,
            },
          }}
        >
          <Box
            sx={{
              // Chat keeps the reading-width column treatment (1000px max).
              // Context analysis takes the full canvas — the parts DataGrid
              // and donut chart need the horizontal room to breathe.
              maxWidth: tab === "context" ? "none" : CONTENT_MAX_WIDTH_PX,
              width: "100%",
              height: tab === "context" ? "100%" : undefined,
              mx: tab === "context" ? 0 : "auto",
              px: CONTENT_X_PADDING,
              py: 3,
              display: tab === "context" ? "flex" : "block",
              flexDirection: tab === "context" ? "column" : undefined,
            }}
          >
            {tab === "chat" ? (
              <ScoutProposalProvider value={proposalContextValue}>
                {loading && messages.length === 0 ? (
                  <Stack spacing={3}>
                    <Skeleton variant="rounded" height={72} />
                    <Skeleton variant="rounded" height={120} />
                    <Skeleton variant="rounded" height={96} />
                  </Stack>
                ) : null}
                {messages.map((message) =>
                  message.role === "user" ? (
                    <UserMessage
                      key={message.id}
                      message={message}
                      showAuthorEmail={showAuthorEmail}
                    />
                  ) : (
                    <AgentMessage
                      key={message.id}
                      message={message}
                      showToolIoExpansion
                      showStaffFeedback
                    />
                  )
                )}
                {!loading && messages.length === 0 && !isInitialLoading ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 6 }}
                  >
                    No messages yet.
                  </Typography>
                ) : null}
              </ScoutProposalProvider>
            ) : (
              <ScoutConversationContextAnalysis conversationId={conversationId} />
            )}
          </Box>
        </Box>
      </Stack>
    </ScoutDeepLinkProvider>
  );
}

interface ConversationUsageProps {
  model?: string | null;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  estimatedCostUsd: number;
}

function ConversationHeader({
  loading,
  title,
  tenantName,
  tenantId,
  userEmail,
  userId,
  userName,
  status,
  createdAt,
  updatedAt,
  conversationId,
  streamStatus,
  usage,
}: {
  loading: boolean;
  title: string | null | undefined;
  tenantName: string | null | undefined;
  tenantId: string | null | undefined;
  userEmail: string | null | undefined;
  userId: string | null | undefined;
  userName: string | null | undefined;
  status: string | null | undefined;
  createdAt: string | null | undefined;
  updatedAt: string | null | undefined;
  conversationId: string;
  streamStatus: string | undefined;
  usage: ConversationUsageProps | null | undefined;
}) {
  const [expanded, setExpanded] = useState(true);

  if (loading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="text" width="80%" />
      </Stack>
    );
  }

  const liveBadge = streamStatus === "connected" || streamStatus === "connecting";
  const formattedUpdated = updatedAt ? DateTime.fromISO(String(updatedAt)).toRelative() : undefined;
  const formattedCreated = createdAt
    ? DateTime.fromISO(String(createdAt)).toLocaleString(DateTime.DATE_MED)
    : undefined;
  const userDisplay = userName && userEmail ? `${userName} (${userEmail})` : userEmail || userId;
  const customerViewHref =
    tenantId != null && tenantId.length > 0 ? `/o/${tenantId}/scout/${conversationId}` : undefined;

  return (
    <Stack spacing={expanded ? 1.25 : 0} sx={{ width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        onClick={() => setExpanded((current) => !current)}
        role="button"
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse conversation header" : "Expand conversation header"}
        sx={{
          width: "100%",
          cursor: "pointer",
          userSelect: "none",
          borderRadius: 1,
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <Typography variant="h5" sx={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
          {title || (
            <Box component="span" sx={{ color: "text.disabled" }}>
              (untitled conversation)
            </Box>
          )}
        </Typography>
        {customerViewHref ? (
          <Tooltip title="Open in Customer view">
            <IconButton
              component={RouterLink}
              to={customerViewHref}
              size="small"
              aria-label="Open in Customer view"
              onClick={(event) => event.stopPropagation()}
            >
              <ReadMore fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        {liveBadge ? (
          <Chip
            size="small"
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CircularProgress size={10} thickness={6} />
                <span>Live</span>
              </Stack>
            }
            color="success"
            variant="outlined"
          />
        ) : null}
        {status ? (
          <Chip
            size="small"
            label={status}
            variant="outlined"
            color={status === "ACTIVE" ? "primary" : "default"}
          />
        ) : null}
        {expanded ? (
          <ExpandLess fontSize="small" color="action" />
        ) : (
          <ExpandMore fontSize="small" color="action" />
        )}
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: "100%" }}>
        <Stack spacing={1.25} sx={{ width: "100%" }}>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap rowGap={0.5}>
            <FieldPair label="Tenant" value={tenantName ?? "(none)"} />
            <FieldPair label="User" value={userDisplay ?? "(unknown)"} />
            {formattedUpdated ? <FieldPair label="Updated" value={formattedUpdated} /> : null}
            {formattedCreated ? <FieldPair label="Created" value={formattedCreated} /> : null}
            {usage ? (
              <>
                <FieldPair label="Model" value={usage.model ?? "—"} />
                <FieldPair label="Estimated cost" value={formatUsdCost(usage.estimatedCostUsd)} />
              </>
            ) : null}
          </Stack>
          {usage && sumUsageTokens(usage) > 0 ? (
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap rowGap={0.5}>
              <FieldPair label="Input tokens" value={formatTokensCompact(usage.inputTokens)} />
              <FieldPair label="Output tokens" value={formatTokensCompact(usage.outputTokens)} />
              <FieldPair
                label="Thinking tokens"
                value={formatTokensCompact(usage.thinkingTokens)}
              />
              <FieldPair
                label="Cache read tokens"
                value={formatTokensCompact(usage.cacheReadTokens)}
              />
              <FieldPair
                label="Cache write tokens"
                value={formatTokensCompact(usage.cacheWriteTokens)}
              />
            </Stack>
          ) : null}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap rowGap={0.75}>
            <CopyChip label="conv" value={conversationId} />
            {tenantId ? <CopyChip label="tenant" value={tenantId} /> : null}
            {userId ? <CopyChip label="user" value={userId} /> : null}
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
}

function sumUsageTokens(usage: ConversationUsageProps): number {
  return (
    usage.inputTokens +
    usage.outputTokens +
    usage.thinkingTokens +
    usage.cacheReadTokens +
    usage.cacheWriteTokens
  );
}

// Match `formatTokens` in encamp-scout/core/src/telemetry/pricing.ts.
function formatTokensCompact(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(2)}M`;
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(1)}K`;
  }
  return tokens.toString();
}

function formatUsdCost(value: number): string {
  if (!Number.isFinite(value) || value === 0) {
    return "$0.0000";
  }
  return `$${value.toFixed(4)}`;
}

function FieldPair({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline">
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.primary" }}>
        {value}
      </Typography>
    </Stack>
  );
}
